import { Box, CircularProgress, Typography } from '@mui/material';
import { usePromptsQuery, useTicketOrganization } from '@tymely/services';
import { useMemo, useState } from 'react';

import { ExpandableSection } from './ExpandableSection';
import { SystemPromptEventProps } from '../types';

export const SystemPromptEvent = ({
    isExpanded,
    onToggle,
    globalSystemPrompt,
    clientSystemPrompt,
    knowledgeBaseSystemPrompt,
}: SystemPromptEventProps) => {
    const { config } = useTicketOrganization();
    const globalPromptName = config?.conversation?.global_guidelines_prompt_name;
    const clientPromptName = config?.conversation?.client_guidelines_prompt_name;
    const outputValidationPromptName = config?.conversation?.output_validation_prompt_name;
    const outputValidationPromptQuery = usePromptsQuery(undefined, outputValidationPromptName, false);

    const outputValidationPrompt = useMemo(() => {
        if (!outputValidationPromptQuery.data) return null;
        return outputValidationPromptQuery.data?.[0]?.current || null;
    }, [outputValidationPromptName, outputValidationPromptQuery.data]);

    const [expanded, setExpanded] = useState({
        global: false,
        client: false,
        knowledgeBase: false,
        outputValidation: false,
    });

    const handleToggle = (key: keyof typeof expanded) => () => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

    const promptSections = useMemo(
        () => [
            {
                key: 'outputValidation',
                promptName: outputValidationPromptName,
                isLoading: outputValidationPromptQuery.isFetching,
                prompt: outputValidationPrompt?.system_prompt,
                expanded: expanded.outputValidation,
                onToggle: handleToggle('outputValidation'),
            },
            {
                key: 'global',
                promptName: globalPromptName,
                prompt: globalSystemPrompt,
                expanded: expanded.global,
                onToggle: handleToggle('global'),
            },
            {
                key: 'client',
                promptName: clientPromptName,
                prompt: clientSystemPrompt,
                expanded: expanded.client,
                onToggle: handleToggle('client'),
            },
            {
                key: 'knowledgeBase',
                promptName: 'KNOWLEDGE_BASE',
                prompt: knowledgeBaseSystemPrompt,
                expanded: expanded.knowledgeBase,
                onToggle: handleToggle('knowledgeBase'),
            },
        ],
        [
            globalPromptName,
            expanded.global,
            handleToggle,
            clientPromptName,
            expanded.client,
            outputValidationPromptName,
            expanded.outputValidation,
            expanded.knowledgeBase,
            knowledgeBaseSystemPrompt,
            globalSystemPrompt,
            clientSystemPrompt,
        ],
    );

    return (
        <Box>
            <ExpandableSection title="" isExpanded={isExpanded} onToggle={onToggle} hideArrow>
                {promptSections.map(({ key, promptName, isLoading, prompt, expanded, onToggle }) => {
                    if (!promptName) return null;
                    if (isLoading) {
                        return (
                            <Box key={key} sx={{ display: 'flex', mt: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {promptName}
                                </Typography>
                                <CircularProgress size={18} sx={{ ml: 2 }} />
                            </Box>
                        );
                    }
                    if (prompt) {
                        return (
                            <ExpandableSection key={key} title={promptName} isExpanded={expanded} onToggle={onToggle}>
                                <Box sx={{ mt: 1, ml: 2 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {prompt}
                                    </Typography>
                                </Box>
                            </ExpandableSection>
                        );
                    }
                    return null;
                })}
            </ExpandableSection>
        </Box>
    );
};
